// =============================================================================
// io/sync/importTrades.ts — THE FIRST WRITE PATH IN THIS CLIENT.
// -----------------------------------------------------------------------------
// Inserting parsed trades. Not a diff, not a save — an INSERT of rows that did
// not exist, which is why it is its own module rather than a branch of
// `planTradeSave`.
//
// ── 🚨 WHY `markNew` IS THE HONEST LEDGER CALL HERE ──
//
// `tradeToDb` refuses any trade whose id is not in the hydration ledger (D46),
// because a partially-read row saved back destroys the columns it never carried.
// An imported trade has NO CLOUD ROW to destroy: it was built here, from a file,
// complete. `markNew` says exactly that, and it is the ledger method that exists
// for this case.
//
// ⚠ `trustEveryTrade` is NOT used and must not be. Its docstring is explicit that
// it is for seeding a test database, and its `reason` parameter exists so a
// production call site has to justify itself in source. "Import" is not a
// justification — `markNew` already covers it, per id, without blanket trust.
//
// ── INSERT, NOT UPSERT ──
//
// Ids are generated here, so a collision means a bug rather than a re-import, and
// `upsert` would silently overwrite whatever it hit. An insert fails loudly
// instead — which is the correct outcome for an id collision on the very first
// write path in the app.
//
// ── CHUNKED, AND PARTIAL SUCCESS IS REPORTED ──
//
// A 900-trade broker export exceeds a comfortable request size. Each chunk is its
// own insert, and a failure mid-way leaves earlier chunks WRITTEN. That is
// reported rather than hidden: the user needs to know some rows landed, or a
// retry doubles them — and the dedupe only protects them if they know to re-run
// the same file rather than assume nothing happened.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Trade } from '../../domain/types';
import { tradeToDb } from './serialize';
import type { HydrationLedger } from './hydration';
import { reportFailureAs } from '../diagnostics/errorLog';

/** Rows per insert. Well under any practical request-size limit. */
export const IMPORT_CHUNK = 200;

export interface ImportWriteResult {
  /** Rows the database accepted. */
  inserted: number;
  /** Requests it took. */
  chunks: number;
  /**
   * ⚠ Non-null means SOME ROWS WERE ALREADY WRITTEN before this failed. The
   * caller must say so — "the import failed" alone invites a retry that doubles
   * everything that did land.
   */
  error: string | null;
}

/**
 * What stamps a batch onto every trade in one import.
 *
 * ⚠ ALL FOUR RIDE `extras`. None of them is a column, and none should become
 * one: `tradeToDb` routes any non-`Trade` key into the `extras` jsonb
 * automatically, which is the same mechanism `executions` uses and the same one
 * beta stamps through. Adding columns would mean a migration for a feature whose
 * whole design is that batches are DERIVED rather than stored (see
 * `io/sync/importBatches.ts`).
 */
export interface ImportBatch {
  batchId: string;
  /**
   * ⚠ AN ISO STRING, because that is what beta writes and the two clients share
   * one database (D5).
   *
   * The first version of this used epoch millis, which reads better and is
   * wrong: Josh's book already contains batches stamped by beta —
   * `"2026-07-29T12:29:54.216Z"` — and `Number(iso)` is NaN, so every one of
   * them would have sorted last with an em-dash for a date. Found by looking at
   * the real rows, not by reading beta's `now()`.
   */
  importedAt: string;
  filename: string;
  format: string;
}

/** A new batch stamp. `crypto.randomUUID` is in every supported browser. */
export function newImportBatch(filename: string, format: string, nowIso: string): ImportBatch {
  return { batchId: crypto.randomUUID(), importedAt: nowIso, filename, format };
}

/**
 * Insert freshly parsed trades.
 *
 * @param trades complete, locally built trades — never projected rows
 * @param ledger marked with `markNew` per trade, here, so the caller cannot skip it
 */
export async function insertImportedTrades(
  sb: SupabaseClient,
  trades: readonly Trade[],
  userId: string,
  ledger: HydrationLedger,
  batch: ImportBatch | null = null,
): Promise<ImportWriteResult> {
  if (!trades.length) return { inserted: 0, chunks: 0, error: null };

  // ⚠ Marked BEFORE serialising, and marked here rather than at the call site.
  // `tradeToDb` throws on an unmarked id, so forgetting this is a hard failure
  // rather than a silent partial write — but the point of doing it inside this
  // function is that no caller has to remember, and none can decide not to.
  const rows = trades.map((t) => {
    ledger.markNew(String(t.id));
    // ⚠ STAMPED HERE, at the write boundary, for the same reason the ledger is:
    // a caller that forgets produces trades that can never be undone, and
    // nothing about them looks wrong until someone needs to undo. Beta stamps
    // inside its own add-function for the same reason.
    //
    // ⚠ `batch` is nullable and that is deliberate — the realtime logger push
    // adds trades with no batch, exactly as beta's `if (batchMeta)` guard allows.
    // An unstamped trade is simply not part of any batch, which is true.
    const stamped = batch
      ? {
        ...t,
        importBatchId: batch.batchId,
        importedAt: batch.importedAt,
        importFilename: batch.filename,
        ...(batch.format ? { importFormat: batch.format } : {}),
      } as Trade
      : t;
    return tradeToDb(stamped, userId, ledger);
  });

  let inserted = 0;
  let chunks = 0;

  for (let i = 0; i < rows.length; i += IMPORT_CHUNK) {
    const slice = rows.slice(i, i + IMPORT_CHUNK);
    chunks++;

    // ⚠ INSERT, not upsert. See the header: a collision is a bug, and an upsert
    // would overwrite a real trade rather than tell anyone.
    const { error } = await sb.from('trades').insert(slice);

    if (error) {
      // ⚠ Every id in this chunk is FORGOTTEN. The rows did not land, so the
      // ledger must not keep claiming they are saveable — a later save would then
      // write a trade the database has never seen, under an id it does not have.
      for (const t of trades.slice(i, i + IMPORT_CHUNK)) ledger.forget(String(t.id));

      // ⚠ Logged as well as returned. This is the one failure in the app that
      // leaves the database HALF-WRITTEN, so the log line is the only lasting
      // record of where the run stopped once the toast is gone.
      return {
        inserted,
        chunks,
        error: reportFailureAs(
          'import trades',
          error,
          `Import failed on chunk ${chunks} (rows ${i + 1}–${i + slice.length}): ` +
          `${error.message}. ${inserted} trade${inserted === 1 ? '' : 's'} ` +
          `${inserted === 1 ? 'was' : 'were'} already written and ${inserted ? 'remain' : 'nothing remains'} ` +
          `in your book — re-running the same file is safe, the duplicate check will skip them.`,
        ),
      };
    }

    inserted += slice.length;
  }

  return { inserted, chunks, error: null };
}
